import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Tag,
  Camera,
  Trash,
  PencilSimple,
  FileXls,
  FilePdf,
  X,
  Check,
  Barcode,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { CalculatorPhoto } from "@/components/calculator/CalculatorPhoto";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const saveError = (error) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : Array.isArray(detail)
    ? detail.map((item) => item.msg || "Controleer de invoer").join(" · ")
    : "Fout bij opslaan. Probeer opnieuw.";
};

// ── Helpers ──────────────────────────────────────────────
const fmtEUR = (v) =>
  Number(v || 0).toLocaleString("nl-BE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// ── Barcode Camera Scanner Modal (Native BarcodeDetector + ZXing fallback) ─
function BarcodeScannerModal({ open, onClose, onScan }) {
  const videoRef = useRef(null);
  const stopFnRef = useRef(null);
  const [error, setError] = useState("");
  const [engine, setEngine] = useState(""); // "native" | "zxing"

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setEngine("");

    const handleResult = (text) => {
      if (cancelled || !text) return;
      cancelled = true;
      cleanup();
      onScan(String(text).trim());
    };

    const cleanup = () => {
      try {
        stopFnRef.current?.();
      } catch {}
      stopFnRef.current = null;
      if (videoRef.current?.srcObject) {
        try {
          videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
        } catch {}
        videoRef.current.srcObject = null;
      }
    };

    (async () => {
      try {
        // 1) Probeer native BarcodeDetector (iOS 17+ Safari, Chrome Android)
        const hasNative = "BarcodeDetector" in window;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        if (!videoRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", "true");
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});

        if (hasNative) {
          try {
            const Detector = window.BarcodeDetector;
            const supported = (await Detector.getSupportedFormats?.()) || [];
            const preferred = [
              "ean_13", "ean_8", "upc_a", "upc_e",
              "code_128", "code_39", "code_93", "codabar",
              "itf", "qr_code", "data_matrix", "pdf417", "aztec",
            ];
            const formats = preferred.filter((f) =>
              supported.length === 0 ? true : supported.includes(f)
            );
            const detector = new Detector({ formats });
            setEngine("native");

            let running = true;
            stopFnRef.current = () => {
              running = false;
            };

            const tick = async () => {
              if (!running || cancelled || !videoRef.current) return;
              try {
                const results = await detector.detect(videoRef.current);
                if (results && results[0]?.rawValue) {
                  handleResult(results[0].rawValue);
                  return;
                }
              } catch {
                // ignore per-frame error
              }
              if (running && !cancelled) {
                requestAnimationFrame(tick);
              }
            };
            requestAnimationFrame(tick);
            return;
          } catch {
            // fall through to zxing
          }
        }

        // 2) Fallback: @zxing/browser (alle platforms)
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        if (cancelled) return;
        const reader = new BrowserMultiFormatReader();
        setEngine("zxing");

        const controls = await reader.decodeFromVideoElement(
          videoRef.current,
          (result) => {
            if (result?.getText) {
              handleResult(result.getText());
            }
          }
        );
        stopFnRef.current = () => {
          try {
            controls.stop();
          } catch {}
        };
      } catch (e) {
        if (!cancelled) {
          setError(
            "Camera niet beschikbaar. Geef toestemming voor camera-toegang of gebruik een externe scanner."
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [open, onScan]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera size={18} weight="bold" /> Scan barcode / QR-code
          </DialogTitle>
          <DialogDescription className="text-xs">
            Richt de camera op de EAN, EAN-128, Code-128 of QR-code. Herkenning
            is automatisch.
            {engine === "native" && (
              <span className="ml-1 text-[#002FA7] font-semibold">
                · Native detector actief
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <div className="text-sm text-[#E53E3E] py-4" data-testid="scanner-error">
            {error}
          </div>
        ) : (
          <div className="relative w-full aspect-[16/10] bg-black rounded-sm overflow-hidden">
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="scanner-video"
              playsInline
              muted
              autoPlay
            />
            {/* Guide overlay */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-[78%] h-[38%] border-2 border-white/80 rounded-sm shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="scanner-close-btn">
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Modal ───────────────────────────────────────────
function EditDialog({ open, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    barcode: "",
    artikelnaam: "",
    normale_prijs: "",
    promo_prijs: "",
    notitie: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        barcode: item.barcode || "",
        artikelnaam: item.artikelnaam || "",
        normale_prijs: String(item.normale_prijs ?? ""),
        promo_prijs: String(item.promo_prijs ?? ""),
        notitie: item.notitie || "",
      });
    }
  }, [item]);

  const save = async () => {
    const n = parseFloat(form.normale_prijs);
    const p = parseFloat(form.promo_prijs);
    if (!isFinite(n) || !isFinite(p) || n <= 0 || p <= 0) {
      toast.error("Voer geldige prijzen in");
      return;
    }
    if (p >= n) {
      toast.error("Promoprijs moet lager zijn dan normale prijs");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/calculator/kortingen/${item.id}`, {
        barcode: form.barcode,
        artikelnaam: form.artikelnaam,
        normale_prijs: n,
        promo_prijs: p,
        notitie: form.notitie,
      });
      toast.success("Bijgewerkt");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(saveError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle>Korting bewerken</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Barcode</Label>
            <Input
              data-testid="edit-barcode"
              value={form.barcode}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
              autoCapitalize="characters"
            />
          </div>
          <div>
            <Label className="text-xs">Artikelnaam</Label>
            <Input
              data-testid="edit-artikelnaam"
              value={form.artikelnaam}
              onChange={(e) => setForm({ ...form, artikelnaam: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Normale prijs (€)</Label>
              <Input
                data-testid="edit-normaal"
                type="number"
                step="0.01"
                value={form.normale_prijs}
                onChange={(e) => setForm({ ...form, normale_prijs: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Promoprijs (€)</Label>
              <Input
                data-testid="edit-promo"
                type="number"
                step="0.01"
                value={form.promo_prijs}
                onChange={(e) => setForm({ ...form, promo_prijs: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notitie</Label>
            <Input
              data-testid="edit-notitie"
              value={form.notitie}
              onChange={(e) => setForm({ ...form, notitie: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel">
            Annuleren
          </Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save">
            {saving ? "Opslaan..." : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ────────────────────────────────────────────
export default function Kortingscalculator() {
  const user = useAuth();

  const [barcode, setBarcode] = useState("");
  const [artikelnaam, setArtikelnaam] = useState("");
  const [normaal, setNormaal] = useState("");
  const [promo, setPromo] = useState("");
  const [notitie, setNotitie] = useState("");

  const [scannerOpen, setScannerOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [confirmBatchOpen, setConfirmBatchOpen] = useState(false);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);

  const [photoKey, setPhotoKey] = useState(0);
  const barcodeInputRef = useRef(null);

  // Live calc
  const calc = useMemo(() => {
    const n = parseFloat(normaal);
    const p = parseFloat(promo);
    if (!isFinite(n) || !isFinite(p) || n <= 0 || p <= 0 || p >= n) {
      return { ok: false, pct: 0, saving: 0, n, p };
    }
    return { ok: true, pct: ((n - p) / n) * 100, saving: n - p, n, p };
  }, [normaal, promo]);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/calculator/kortingen`);
      setHistory(res.data || []);
    } catch (e) {
      toast.error("Fout bij laden geschiedenis");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Auto-focus barcode input on mount (compatible with USB-HID scanners)
  useEffect(() => {
    barcodeInputRef.current?.focus();
  }, []);

  // ── Camera scanner ──
  const handleScanResult = (text) => {
    setBarcode(text);
    setScannerOpen(false);
    toast.success(`Code gescand: ${text}`);
    // Move focus to normaal price input after scan
    setTimeout(() => {
      document.getElementById("normaal-input")?.focus();
    }, 50);
  };

  // ── AI vision price extraction ──
  const handleLabelResult = (data) => {
    // A new label replaces the previous label as a unit, never mixes old and new article data.
    setBarcode(data.artikelcode || "");
    setArtikelnaam(data.artikelnaam || "");
    setNormaal(data.normale_prijs ? String(data.normale_prijs) : "");
    setPromo(data.promo_prijs ? String(data.promo_prijs) : "");
    if (data.normale_prijs || data.artikelnaam || data.artikelcode) toast.success("Etiketgegevens ingevuld");
  };

  // Apply a preset discount % to compute promo price
  const applyDiscountPreset = (pct) => {
    const n = parseFloat(normaal);
    if (!isFinite(n) || n <= 0) {
      toast.error("Vul eerst de normale prijs in");
      return;
    }
    const promoVal = n * (1 - pct / 100);
    // Round to 2 decimals, ending on .99 looks natural — keep simple .toFixed(2)
    setPromo(promoVal.toFixed(2));
  };

  // ── Save calculation ──
  // Check if the same artikelcode/barcode was already saved today
  const findDuplicateToday = (code) => {
    if (!code) return null;
    const today = new Date().toDateString();
    return (
      history.find(
        (h) =>
          (h.barcode || "").trim().toLowerCase() === code.trim().toLowerCase() &&
          h.created_at &&
          new Date(h.created_at).toDateString() === today
      ) || null
    );
  };

  const [duplicateConfirm, setDuplicateConfirm] = useState(null); // {existing, payload}

  const performSave = async ({ payload, existingId }) => {
    if (!payload || saving) return;
    setSaving(true);
    try {
      if (existingId) {
        await axios.put(`${API}/calculator/kortingen/${existingId}`, {
          barcode: payload.barcode,
          artikelnaam: payload.artikelnaam,
          normale_prijs: payload.normale_prijs,
          promo_prijs: payload.promo_prijs,
          notitie: payload.notitie,
        });
        toast.success("Bestaande korting bijgewerkt");
      } else {
        await axios.post(`${API}/calculator/kortingen`, payload);
        toast.success("Korting opgeslagen");
      }
      setBarcode("");
      setArtikelnaam("");
      setNormaal("");
      setPromo("");
      setNotitie("");
      setPhotoKey((key) => key + 1);
      fetchHistory();
      setTimeout(() => barcodeInputRef.current?.focus(), 50);
    } catch (e) {
      toast.error(saveError(e));
    } finally {
      setSaving(false);
      setDuplicateConfirm(null);
    }
  };

  const handleSave = async () => {
    if (aiLoading || saving) return;
    if (!calc.ok) {
      toast.error("Voer geldige prijzen in (aanbiedingprijs lager dan normale prijs)");
      return;
    }
    const payload = {
      barcode: barcode.trim(),
      artikelnaam: artikelnaam.trim(),
      normale_prijs: calc.n,
      promo_prijs: calc.p,
      notitie: notitie.trim(),
      medewerker_id: user?.id || "",
      medewerker_naam: `${user?.voornaam || ""} ${user?.achternaam || ""}`.trim(),
    };

    const existing = findDuplicateToday(payload.barcode);
    if (existing) {
      setDuplicateConfirm({ existing, payload });
      return;
    }
    await performSave({ payload });
  };

  const handleResetForm = () => {
    setPhotoKey((key) => key + 1);
    setAiLoading(false);
    setBarcode("");
    setArtikelnaam("");
    setNormaal("");
    setPromo("");
    setNotitie("");
    barcodeInputRef.current?.focus();
  };

  // ── Selection ──
  const toggleSelect = (id) => {
    setSelected((s) => {
      const ns = new Set(s);
      if (ns.has(id)) ns.delete(id);
      else ns.add(id);
      return ns;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === history.length) setSelected(new Set());
    else setSelected(new Set(history.map((h) => h.id)));
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    try {
      await axios.delete(`${API}/calculator/kortingen/batch`, {
        data: { ids: Array.from(selected) },
      });
      toast.success(`${selected.size} verwijderd`);
      setSelected(new Set());
      fetchHistory();
    } catch (e) {
      toast.error("Fout bij verwijderen");
    } finally {
      setConfirmBatchOpen(false);
    }
  };

  const deleteAll = async () => {
    if (history.length === 0) return;
    try {
      await axios.delete(`${API}/calculator/kortingen/batch`, {
        data: { ids: history.map((h) => h.id) },
      });
      toast.success("Geschiedenis gewist");
      setSelected(new Set());
      fetchHistory();
    } catch (e) {
      toast.error("Fout bij wissen");
    } finally {
      setConfirmAllOpen(false);
    }
  };

  const exportExcel = async () => {
    try {
      const res = await axios.get(`${API}/calculator/kortingen/export`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kortingen_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export mislukt");
    }
  };

  const exportPdf = async () => {
    try {
      const res = await axios.get(`${API}/calculator/kortingen/export-pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kortingen_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF export mislukt");
    }
  };

  // Auto-advance on barcode (length >= 8 → focus normaal)
  const onBarcodeKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // USB scanners eindigen vaak met Enter
      document.getElementById("normaal-input")?.focus();
    }
  };

  return (
    <div className="space-y-5" data-testid="kortingscalculator-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-2xl font-black tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            <Tag size={24} weight="bold" className="text-[#002FA7]" />
            Kortingscalculator
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Scan EAN128, lees prijs via AI of typ handmatig — bereken het kortingspercentage.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 min-w-0">
        {/* ── Calculator Card ── */}
        <div
          className="bg-white border border-border rounded-sm p-5 min-w-0"
          data-testid="calc-card"
        >
          {/* Barcode row */}
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
            Artikelcode (scan, typ of camera)
          </Label>
          <div className="flex gap-2 mt-1.5 mb-3">
            <Input
              ref={barcodeInputRef}
              id="barcode-input"
              data-testid="barcode-input"
              value={barcode}
              disabled={aiLoading || saving}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={onBarcodeKey}
              placeholder="bv. 24WSC021 of 8712345678901"
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => setScannerOpen(true)}
              disabled={aiLoading || saving}
              title="Scan via camera"
              data-testid="open-scanner-btn"
            >
              <Camera size={18} weight="bold" />
            </Button>
          </div>

          <div className="mb-3">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Artikelnaam (optioneel)
            </Label>
            <Input
              data-testid="artikelnaam-input"
              value={artikelnaam}
              disabled={aiLoading || saving}
              onChange={(e) => setArtikelnaam(e.target.value)}
              placeholder="bv. Coca-Cola 1.5L"
              className="mt-1.5"
            />
          </div>

          <CalculatorPhoto key={photoKey} onExtract={handleLabelResult} onBusy={setAiLoading} disabled={saving} />

          {/* Price row */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="min-w-0">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#002FA7] whitespace-nowrap">
                Normaal (€) *
              </Label>
              <Input
                id="normaal-input"
                data-testid="normaal-input"
                type="number"
                step="0.01"
                min="0"
                value={normaal}
                disabled={aiLoading || saving}
                onChange={(e) => setNormaal(e.target.value)}
                placeholder=""
                className="mt-1.5 bg-[#EAF1FF] border-[#002FA7]/40 focus:bg-white focus:border-[#002FA7] text-base font-semibold"
              />
            </div>
            <div className="min-w-0">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#1F7A1F] whitespace-nowrap">
                Aanbieding (€) *
              </Label>
              <Input
                id="promo-input"
                data-testid="promo-input"
                type="number"
                step="0.01"
                min="0"
                value={promo}
                disabled={aiLoading || saving}
                onChange={(e) => setPromo(e.target.value)}
                placeholder=""
                className="mt-1.5 bg-[#F0FAEC] border-[#1F7A1F]/40 focus:bg-white focus:border-[#1F7A1F] text-base font-semibold"
              />
            </div>
          </div>

          <div className="mb-3">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Notitie (optioneel)
            </Label>
            <Input
              data-testid="notitie-input"
              value={notitie}
              onChange={(e) => setNotitie(e.target.value)}
              placeholder="Bv. promo week 12"
              className="mt-1.5"
            />
          </div>

          {/* Quick discount presets */}
          <div className="mb-3">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Snelle korting
            </Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="discount-presets">
              {[10, 20, 30, 40, 50].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => applyDiscountPreset(p)}
                  disabled={aiLoading || saving || !normaal || parseFloat(normaal) <= 0}
                  className="flex-1 min-w-[52px] px-2 py-1.5 text-xs font-bold rounded-sm border border-border bg-white hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid={`preset-${p}`}
                >
                  -{p}%
                </button>
              ))}
            </div>
          </div>

          {/* Result */}
          {calc.ok ? (
            <div
              className="bg-[#F0FAEC] border border-[#9DD380] rounded-sm p-4 mb-3"
              data-testid="result-card"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="text-[11px] font-mono bg-white border border-border px-2 py-0.5 rounded-sm min-w-0 break-all" data-testid="result-article-code">
                  {barcode || "Geen code"}
                </span>
                <button
                  onClick={handleResetForm}
                  className="text-xs text-muted-foreground hover:underline shrink-0"
                  data-testid="reset-form-btn"
                >
                  Wissen
                </button>
              </div>
              <div
                className="text-5xl font-black text-[#1F7A1F] leading-none mt-2"
                data-testid="result-pct"
              >
                {calc.pct.toFixed(1)}%
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">kortingspercentage</p>
              <div className="grid grid-cols-2 gap-2 border-t border-[#C8E6B8] pt-3">
                <div>
                  <p className="text-[11px] text-muted-foreground">Normale prijs</p>
                  <p className="text-base font-semibold">€ {fmtEUR(calc.n)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Aanbiedingprijs</p>
                  <p
                    className="text-base font-semibold text-[#1F7A1F]"
                    data-testid="result-promo"
                  >
                    € {fmtEUR(calc.p)}
                  </p>
                </div>
              </div>
              <div className="inline-block mt-3 bg-[#E8F5E9] text-[#1F7A1F] text-xs font-bold px-3 py-1 rounded-full">
                Je bespaart: € {fmtEUR(calc.saving)}
              </div>
            </div>
          ) : (
            (normaal || promo) && (
              <div
                className="text-xs text-[#E53E3E] mb-3"
                data-testid="result-error"
              >
                Voer geldige prijzen in (aanbiedingprijs moet lager zijn dan normale prijs).
              </div>
            )
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={!calc.ok || saving || aiLoading}
              className="flex-1 min-w-0 h-auto min-h-9 whitespace-normal py-2 bg-[#002FA7] hover:bg-[#002FA7]/90"
              data-testid="save-korting-btn"
            >
              <Check size={16} weight="bold" className="mr-1.5" />
              {saving ? "Opslaan..." : "Opslaan in geschiedenis"}
            </Button>
            <Button
              variant="outline"
              onClick={handleResetForm}
              data-testid="clear-form-btn"
            >
              <X size={16} />
            </Button>
          </div>
        </div>

        {/* ── History Card ── */}
        <div
          className="bg-white border border-border rounded-sm p-5 min-w-0"
          data-testid="history-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-testid="history-toolbar">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
                <Barcode size={16} weight="bold" />
                Geschiedenis
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {history.length} berekening{history.length === 1 ? "" : "en"}
                {selected.size > 0 ? ` · ${selected.size} geselecteerd` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="history-actions">
              <Button
                size="sm"
                variant="outline"
                disabled={history.length === 0}
                onClick={exportExcel}
                data-testid="export-excel-btn"
                title="Exporteer naar Excel"
              >
                <FileXls size={14} weight="bold" className="mr-1" />
                Excel
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={history.length === 0}
                onClick={exportPdf}
                data-testid="export-pdf-btn"
                title="Exporteer naar PDF"
              >
                <FilePdf size={14} weight="bold" className="mr-1" />
                PDF
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={selected.size === 0}
                onClick={() => setConfirmBatchOpen(true)}
                data-testid="batch-delete-btn"
                title="Verwijder selectie"
              >
                <Trash size={14} weight="bold" className="mr-1" />
                Selectie ({selected.size})
              </Button>
            </div>
          </div>

          {loadingHistory ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Laden...</div>
          ) : history.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">
              Nog geen berekeningen opgeslagen.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto -mx-5">
                <table className="w-full text-xs">
                  <thead className="bg-[#F4F5F7] border-y border-border">
                    <tr>
                      <th className="px-3 py-2 text-left w-8">
                        <Checkbox
                          checked={
                            history.length > 0 && selected.size === history.length
                          }
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="px-2 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">
                        Datum ingave
                      </th>
                      <th className="px-2 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">
                        Code
                      </th>
                      <th className="px-2 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground">
                        Normaal
                      </th>
                      <th className="px-2 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground">
                        Promo
                      </th>
                      <th className="px-2 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground">
                        %
                      </th>
                      <th className="px-2 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground w-16">
                        Acties
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr
                        key={h.id}
                        className={`border-b border-border hover:bg-muted/40 ${
                          selected.has(h.id) ? "bg-[#002FA7]/5" : ""
                        }`}
                        data-testid={`history-row-${h.id}`}
                      >
                        <td className="px-3 py-2">
                          <Checkbox
                            checked={selected.has(h.id)}
                            onCheckedChange={() => toggleSelect(h.id)}
                            data-testid={`select-${h.id}`}
                          />
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-muted-foreground">
                          {fmtDateTime(h.created_at)}
                        </td>
                        <td className="px-2 py-2 font-mono">
                          <div className="truncate max-w-[140px]" title={h.barcode}>
                            {h.barcode || "—"}
                          </div>
                          {h.artikelnaam && (
                            <div className="text-[10px] text-muted-foreground truncate max-w-[140px]">
                              {h.artikelnaam}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          € {fmtEUR(h.normale_prijs)}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-[#1F7A1F] font-semibold">
                          € {fmtEUR(h.promo_prijs)}
                        </td>
                        <td className="px-2 py-2 text-right">
                          <Badge className="bg-[#E8F5E9] text-[#1F7A1F] hover:bg-[#E8F5E9] tabular-nums">
                            {Number(h.korting_pct || 0).toFixed(1)}%
                          </Badge>
                        </td>
                        <td className="px-2 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => setEditing(h)}
                              className="p-1 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground"
                              title="Bewerken"
                              data-testid={`edit-${h.id}`}
                            >
                              <PencilSimple size={14} weight="bold" />
                            </button>
                            <button
                              onClick={async () => {
                                if (!window.confirm("Verwijderen?")) return;
                                try {
                                  await axios.delete(
                                    `${API}/calculator/kortingen/${h.id}`
                                  );
                                  toast.success("Verwijderd");
                                  fetchHistory();
                                } catch {
                                  toast.error("Fout bij verwijderen");
                                }
                              }}
                              className="p-1 rounded-sm hover:bg-muted text-muted-foreground hover:text-[#E53E3E]"
                              title="Verwijderen"
                              data-testid={`delete-${h.id}`}
                            >
                              <Trash size={14} weight="bold" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap gap-2 justify-between items-center mt-3 pt-3 border-t border-border">
                <p className="text-[11px] text-muted-foreground">
                  Tip: gebruik vinkjes om meerdere te selecteren.
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmAllOpen(true)}
                  className="text-[#E53E3E] hover:text-[#E53E3E] hover:bg-[#E53E3E]/10"
                  data-testid="clear-all-btn"
                >
                  Alles wissen
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScanResult}
      />
      <EditDialog
        open={!!editing}
        item={editing}
        onClose={() => setEditing(null)}
        onSaved={fetchHistory}
      />

      {/* Confirm batch delete */}
      <Dialog open={confirmBatchOpen} onOpenChange={setConfirmBatchOpen}>
        <DialogContent data-testid="confirm-batch-dialog">
          <DialogHeader>
            <DialogTitle>Selectie verwijderen?</DialogTitle>
            <DialogDescription>
              Je staat op het punt om {selected.size} berekening
              {selected.size === 1 ? "" : "en"} permanent te verwijderen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmBatchOpen(false)}
              data-testid="confirm-batch-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={deleteSelected}
              className="bg-[#E53E3E] hover:bg-[#E53E3E]/90"
              data-testid="confirm-batch-delete"
            >
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm clear all */}
      <Dialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
        <DialogContent data-testid="confirm-all-dialog">
          <DialogHeader>
            <DialogTitle>Alle geschiedenis wissen?</DialogTitle>
            <DialogDescription>
              Alle {history.length} berekeningen worden permanent verwijderd.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAllOpen(false)}
              data-testid="confirm-all-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={deleteAll}
              className="bg-[#E53E3E] hover:bg-[#E53E3E]/90"
              data-testid="confirm-all-delete"
            >
              Wissen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate today confirm */}
      <Dialog
        open={!!duplicateConfirm}
        onOpenChange={(o) => !o && !saving && setDuplicateConfirm(null)}
      >
        <DialogContent data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle>Reeds ingescand vandaag</DialogTitle>
            <DialogDescription>
              Artikelcode <span className="font-mono font-bold">
                {duplicateConfirm?.existing?.barcode || "—"}
              </span>{" "}
              is vandaag al opgeslagen met{" "}
              <span className="font-semibold">
                € {fmtEUR(duplicateConfirm?.existing?.normale_prijs)} → €{" "}
                {fmtEUR(duplicateConfirm?.existing?.promo_prijs)} (
                {Number(duplicateConfirm?.existing?.korting_pct || 0).toFixed(1)}%)
              </span>
              . Wil je deze bijwerken met de nieuwe waarden?
            </DialogDescription>
          </DialogHeader>
          {duplicateConfirm?.existing && (
            <div className="bg-[#F4F5F7] border border-border rounded-sm p-3 text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nieuw normaal:</span>
                <span className="font-semibold">
                  € {fmtEUR(duplicateConfirm.payload.normale_prijs)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nieuw aanbieding:</span>
                <span className="font-semibold text-[#1F7A1F]">
                  € {fmtEUR(duplicateConfirm.payload.promo_prijs)}
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDuplicateConfirm(null)}
              disabled={saving}
              data-testid="duplicate-cancel"
            >
              Nee
            </Button>
            <Button
              disabled={!duplicateConfirm || saving}
              onClick={() => {
                if (!duplicateConfirm || saving) return;
                performSave({
                  payload: duplicateConfirm.payload,
                  existingId: duplicateConfirm.existing.id,
                });
              }}
              className="bg-[#002FA7] hover:bg-[#002FA7]/90"
              data-testid="duplicate-update"
            >
              Ja, bijwerken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
